import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { postsApi, leaderboardApi, reportsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { Heart, MessageCircle, Plus, Image as ImageIcon, X, Send, Flame, Trash2, Loader2, Trophy, Crown, Flag } from "lucide-react";

const VERDICTS = [
  { v: "gas",  l: "🔥 GAS",  c: "bg-[#15FF73]" },
  { v: "mid",  l: "😐 MID",  c: "bg-[#FFE74C]" },
  { v: "pass", l: "🚫 PASS", c: "bg-[#FF1A8C] text-white" },
];

export default function Community() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [board, setBoard] = useState({ leaders: [], week_of: "", total_posts_this_week: 0 });

  const refresh = async () => {
    try {
      const [p, b] = await Promise.all([postsApi.list(), leaderboardApi.weekly()]);
      setPosts(p);
      setBoard(b);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 30000);
    return () => clearInterval(i);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#15FF73", top: -120, right: -80 }} />
      <div className="blob" style={{ width: 260, height: 260, background: "#FF1A8C", bottom: 100, left: -80, animationDelay: "-7s" }} />
      <NavBar />
      <Toaster position="top-right" theme="light" richColors />
      <div className="max-w-2xl mx-auto px-4 lg:px-6 py-10 relative">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="sticker sticker-magenta mb-2 inline-block">Community</span>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.9] tracking-tight mt-2">
              The <span className="text-rainbow">feed.</span>
            </h1>
          </div>
          <button
            data-testid="community-compose-open"
            onClick={() => user ? setShowCompose(true) : nav("/auth?mode=login")}
            className="btn-x btn-primary"
          >
            <Plus size={14} strokeWidth={2.6} /> Post
          </button>
        </div>

        {/* WEEKLY LEADERBOARD BANNER */}
        {board.leaders?.length > 0 && (
          <div data-testid="leaderboard-banner" className="card-x card-magenta bg-[#FFE74C] p-5 mb-7">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={18} strokeWidth={2.6} className="text-[#FF1A8C]" />
              <span className="font-display text-2xl font-extrabold leading-none">This week's gas</span>
              <span className="ml-auto text-[10px] font-mono-x font-bold uppercase tracking-widest opacity-70">Week of {board.week_of}</span>
            </div>
            <p className="text-xs opacity-70 mb-4">Top 5 most-liked posts. Winners get a 🏆 Gas Boss badge + free flash-deal claim. {board.total_posts_this_week} posts this week.</p>
            <div className="grid grid-cols-5 gap-2">
              {board.leaders.map((l) => {
                const src = l.image_url || (l.image_base64 ? `data:image/jpeg;base64,${l.image_base64}` : "");
                return (
                  <Link key={l.post_id} to={`/community#${l.post_id}`} className="relative aspect-square rounded-xl overflow-hidden border-2 border-[#0B0F0C] bg-[#0B0F0C] hover:scale-105 transition-transform">
                    {src && <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />}
                    <span className="absolute top-1 left-1 sticker sticker-magenta !text-[10px] !px-1.5 !py-0.5">#{l.rank}</span>
                    <div className="absolute bottom-0 left-0 right-0 bg-[#0B0F0C]/85 text-[#FAF6E8] px-1.5 py-1 flex items-center gap-1 text-[10px] font-mono-x font-bold">
                      <Heart size={10} strokeWidth={2.4} fill="#FF1A8C" className="text-[#FF1A8C]" /> {l.like_count}
                    </div>
                    {l.rank === 1 && <Crown size={14} className="absolute top-1 right-1 text-[#FFE74C] drop-shadow" fill="#FFE74C" />}
                  </Link>
                );
              })}
              {/* Fill empties */}
              {Array.from({ length: Math.max(0, 5 - board.leaders.length) }).map((_, i) => (
                <div key={`e${i}`} className="aspect-square rounded-xl border-2 border-dashed border-[#0B0F0C]/30 bg-[#FAF6E8]/40 flex items-center justify-center text-[10px] font-mono-x font-bold opacity-40">empty</div>
              ))}
            </div>
            {user?.role === "admin" && (
              <button
                data-testid="grant-rewards"
                onClick={async () => {
                  try {
                    const r = await leaderboardApi.grantRewards();
                    toast.success(r.already_granted ? `Already granted for ${r.week_key}` : `Granted to ${r.granted} winners`);
                  } catch { toast.error("Failed"); }
                }}
                className="btn-x btn-ghost !py-1.5 !px-3 !text-xs mt-3"
              >
                Grant rewards (admin)
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 opacity-60"><Loader2 size={28} className="mx-auto animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="card-x card-cyan bg-[#FAF6E8] p-12 text-center">
            <div className="text-7xl mb-3 bouncy">🌿</div>
            <h2 className="font-display text-3xl font-extrabold mb-1">First post?</h2>
            <p className="text-sm opacity-70 mb-5">Drop a smoke shot. Tag the strain. Get the GAS verdict.</p>
            <button onClick={() => user ? setShowCompose(true) : nav("/auth?mode=login")} className="btn-x btn-primary">Post one <Plus size={14} /></button>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} onChange={refresh} />
            ))}
          </div>
        )}
      </div>
      {showCompose && <Composer onClose={() => setShowCompose(false)} onPosted={refresh} />}
      <Footer />
    </div>
  );
}

function PostCard({ post, onChange }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [liked, setLiked] = useState(post.likes?.includes(user?.id));
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [comments, setComments] = useState(post.comments || []);
  const [text, setText] = useState("");
  const [showAll, setShowAll] = useState(false);

  const toggleLike = async () => {
    if (!user) { nav("/auth?mode=login"); return; }
    try {
      const r = await postsApi.like(post.id);
      setLiked(r.liked); setLikeCount(r.like_count);
    } catch (e) { /* ignore */ }
  };
  const sendComment = async (e) => {
    e?.preventDefault();
    if (!user) { nav("/auth?mode=login"); return; }
    if (!text.trim()) return;
    try {
      const c = await postsApi.comment(post.id, text);
      setComments([...comments, c]);
      setText("");
    } catch (e) { /* ignore */ }
  };
  const remove = async () => {
    if (!confirm("Delete this post?")) return;
    try {
      await postsApi.remove(post.id);
      onChange?.();
    } catch (e) { /* ignore */ }
  };

  const verdictMeta = VERDICTS.find((v) => v.v === post.verdict);
  const imageSrc = post.image_url || (post.image_base64 ? `data:image/jpeg;base64,${post.image_base64}` : "");
  const visibleComments = showAll ? comments : comments.slice(-2);

  return (
    <div data-testid={`post-${post.id}`} className="card-x card-cyan bg-[#FAF6E8] overflow-hidden">
      {/* header */}
      <div className="flex items-center gap-3 p-4">
        <div className="w-10 h-10 rounded-full bg-[#FF1A8C] border-2 border-[#0B0F0C] flex items-center justify-center text-white text-sm font-extrabold">
          {(post.user_name || "?")[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-extrabold leading-none">{post.user_name}</div>
          <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest opacity-60">{new Date(post.created_at).toLocaleString()}</div>
        </div>
        {verdictMeta && (
          <span className={`sticker ${verdictMeta.c} border-2 border-[#0B0F0C] text-xs`} style={{ transform: "rotate(-2deg)" }}>
            {verdictMeta.l}
          </span>
        )}
        {(user?.id === post.user_id || user?.role === "admin") && (
          <button onClick={remove} className="w-8 h-8 rounded-full border-2 border-[#0B0F0C] flex items-center justify-center hover:bg-[#FF1A8C] hover:text-white"><Trash2 size={11} strokeWidth={2.4} /></button>
        )}
        {user && user?.id !== post.user_id && (
          <button
            data-testid={`post-flag-${post.id}`}
            onClick={async () => {
              const reason = window.prompt("Why are you reporting this post? (admins will review)");
              if (reason === null) return;
              try { await reportsApi.create("post", post.id, reason); toast.success("Reported. Admin notified."); }
              catch { toast.error("Couldn't send report"); }
            }}
            title="Report this post"
            className="w-8 h-8 rounded-full border-2 border-[#0B0F0C] flex items-center justify-center hover:bg-[#FFE74C]"
          ><Flag size={11} strokeWidth={2.4} /></button>
        )}
      </div>
      {/* image */}
      {imageSrc && (
        <div className="aspect-square bg-[#0B0F0C] overflow-hidden border-y-2 border-[#0B0F0C]">
          <img src={imageSrc} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      {/* actions */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-3">
        <button data-testid={`post-like-${post.id}`} onClick={toggleLike} className={`flex items-center gap-1.5 transition-transform active:scale-90 ${liked ? "text-[#FF1A8C]" : "text-[#0B0F0C]"}`}>
          <Heart size={22} strokeWidth={2.4} fill={liked ? "#FF1A8C" : "transparent"} />
          <span className="font-mono-x font-bold text-sm">{likeCount}</span>
        </button>
        <button className="flex items-center gap-1.5"><MessageCircle size={22} strokeWidth={2.4} /><span className="font-mono-x font-bold text-sm">{comments.length}</span></button>
        {post.product_name && <Link to={`/product/${post.product_id}`} className="ml-auto sticker sticker-green text-[10px] !px-2 !py-1"><Flame size={10} /> {post.product_name}</Link>}
      </div>
      {/* caption */}
      {post.caption && (
        <div className="px-4 pb-2">
          <span className="font-bold text-sm mr-1.5">{post.user_name}</span>
          <span className="text-sm whitespace-pre-line leading-relaxed">{post.caption}</span>
        </div>
      )}
      {/* flavor tags */}
      {post.flavor_tags?.length > 0 && (
        <div className="px-4 pb-2 flex flex-wrap gap-1">
          {post.flavor_tags.map((t) => (
            <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border border-[#0B0F0C] uppercase tracking-wider font-mono-x font-bold">{t}</span>
          ))}
        </div>
      )}
      {/* comments */}
      {comments.length > 2 && !showAll && (
        <button onClick={() => setShowAll(true)} className="px-4 text-xs opacity-60 mb-1">View all {comments.length} comments</button>
      )}
      {visibleComments.map((c) => (
        <div key={c.id} className="px-4 py-1 text-sm">
          <span className="font-bold mr-1.5">{c.user_name}</span><span>{c.body}</span>
        </div>
      ))}
      {/* compose */}
      <form onSubmit={sendComment} className="px-4 pt-3 pb-4 flex items-center gap-2 border-t border-[#0B0F0C]/10 mt-2">
        <input
          data-testid={`post-comment-${post.id}`}
          value={text} onChange={(e) => setText(e.target.value)}
          placeholder={user ? "Add a comment…" : "Sign in to comment"}
          disabled={!user}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-[#0B0F0C]/40"
        />
        <button type="submit" disabled={!text.trim()} className="text-[#FF1A8C] disabled:opacity-30"><Send size={16} strokeWidth={2.4} /></button>
      </form>
    </div>
  );
}

function Composer({ onClose, onPosted }) {
  const [caption, setCaption] = useState("");
  const [verdict, setVerdict] = useState(null);
  const [imgB64, setImgB64] = useState("");
  const [busy, setBusy] = useState(false);
  const [tags, setTags] = useState("");

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4_000_000) { toast.error("Image too big — under 4MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setImgB64(reader.result.split(",")[1]);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!imgB64) { toast.error("Add a photo first"); return; }
    setBusy(true);
    try {
      await postsApi.create({
        image_base64: imgB64,
        caption,
        verdict: verdict || null,
        flavor_tags: tags.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 4),
      });
      toast.success("Posted! +5 XP");
      onPosted?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0B0F0C]/85 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="card-x card-magenta bg-[#FAF6E8] p-5 max-w-md w-full">
        <div className="flex items-center mb-3">
          <span className="font-display text-2xl font-extrabold">New post</span>
          <button onClick={onClose} className="ml-auto w-9 h-9 rounded-full border-2 border-[#0B0F0C] flex items-center justify-center hover:bg-[#FF1A8C] hover:text-white"><X size={14} strokeWidth={2.4} /></button>
        </div>

        {imgB64 ? (
          <div className="relative">
            <img src={`data:image/jpeg;base64,${imgB64}`} alt="" className="w-full aspect-square object-cover rounded-2xl border-2 border-[#0B0F0C]" />
            <button onClick={() => setImgB64("")} className="absolute top-2 right-2 w-9 h-9 rounded-full bg-[#FAF6E8] border-2 border-[#0B0F0C] flex items-center justify-center"><X size={13} strokeWidth={2.4} /></button>
          </div>
        ) : (
          <label data-testid="composer-upload" className="block aspect-square border-2 border-dashed border-[#0B0F0C] rounded-2xl bg-[#FAF6E8] flex items-center justify-center cursor-pointer hover:bg-[#FFE74C]/30 text-center">
            <div>
              <ImageIcon size={28} className="mx-auto mb-2" />
              <div className="font-mono-x font-bold uppercase tracking-widest text-xs">Tap to upload a photo</div>
              <div className="text-[10px] opacity-60 mt-1">JPG · PNG · ≤ 4MB</div>
            </div>
            <input type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
        )}

        <textarea
          value={caption} onChange={(e) => setCaption(e.target.value)}
          placeholder="Drop a caption…"
          rows={3}
          className="mt-3 w-full bg-white border-2 border-[#0B0F0C] rounded-2xl p-3 text-sm outline-none focus:shadow-[3px_3px_0_#0B0F0C] transition-all resize-none"
        />

        <input
          value={tags} onChange={(e) => setTags(e.target.value)}
          placeholder="Flavor tags · citrus, pine, sweet"
          className="mt-3 w-full bg-white border-2 border-[#0B0F0C] rounded-full px-4 h-11 text-sm outline-none focus:shadow-[3px_3px_0_#0B0F0C] transition-all"
        />

        <div className="mt-3 flex gap-2">
          {VERDICTS.map((v) => (
            <button key={v.v} onClick={() => setVerdict(verdict === v.v ? null : v.v)} className={`flex-1 py-2 px-3 rounded-full border-2 border-[#0B0F0C] text-xs font-extrabold ${verdict === v.v ? v.c : "bg-[#FAF6E8]"}`}>
              {v.l}
            </button>
          ))}
        </div>

        <button data-testid="composer-submit" onClick={submit} disabled={busy} className="btn-x btn-primary w-full mt-4 justify-center disabled:opacity-50">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Posting…</> : <>Post · +5 XP →</>}
        </button>
      </div>
    </div>
  );
}
